import { motion } from "framer-motion";
import { ArrowDown } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { HERO } from "@/constants/testIds";

const DARK_BG = "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBnb2xkJTIwZ2VvbWV0cmljfGVufDB8fHx8MTc4MDY4MzA5M3ww&ixlib=rb-4.1.0&q=85";
const LIGHT_BG = "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=1920&q=80";

export default function Hero() {
  const { theme } = useTheme();
  const isLight = theme === "light";

  return (
    <section
      data-testid={HERO.section}
      className="relative min-h-[100vh] flex items-center justify-center overflow-hidden noise-overlay pt-20"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={isLight ? LIGHT_BG : DARK_BG}
          alt=""
          className="w-full h-full object-cover transition-opacity duration-500"
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: isLight ? "rgba(247, 243, 237, 0.82)" : "rgba(0, 0, 0, 0.80)",
          }}
        />
      </div>

      <motion.div
        className="absolute top-0 left-1/4 w-px h-full"
        style={{ background: "linear-gradient(to bottom, transparent, var(--sv-divider), transparent)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 2, delay: 1 }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p data-testid={HERO.tagline} className="overline mb-8">
            Engineering High Velocity People Function
          </p>
        </motion.div>

        <motion.h1
          data-testid={HERO.heading}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] max-w-4xl mx-auto"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
        >
          We find what's{" "}
          <span className="metallic-text">slowing</span>
          <br />
          your HR down.
        </motion.h1>

        <motion.p
          data-testid={HERO.description}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-8 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed text-themed-body"
        >
          Before you reorganise, automate or hire — we measure.
          Siveli maps how your people function actually works today,
          finds where time and money are being lost,
          and designs the version that runs the way it should.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            data-testid={HERO.bookConsultationBtn}
            href="#contact"
            className="btn-shine font-bold tracking-wide px-8 py-4 text-sm transition-colors inline-block"
            style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
          >
            Book a Consultation
          </a>
          <a
            data-testid={HERO.learnMoreBtn}
            href="#services"
            className="gold-underline px-8 py-4 text-sm font-medium transition-all duration-300 inline-block text-themed-primary"
            style={{ border: "1px solid var(--sv-border)" }}
          >
            How We Work
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-20 flex items-center justify-center gap-6"
        >
          <div className="h-px w-12" style={{ background: "linear-gradient(to right, transparent, var(--sv-divider))" }} />
          <span className="text-xs text-themed-muted tracking-widest uppercase" style={{ fontFamily: "var(--sv-mono-font)" }}>
            Measure before you transform
          </span>
          <div className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, var(--sv-divider))" }} />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
      >
        <a href="#services" className="text-themed-muted hover:text-themed-gold transition-colors">
          <ArrowDown size={20} weight="bold" className="animate-bounce" />
        </a>
      </motion.div>
    </section>
  );
}
